// client/src/pages/SellerOrders.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  Box, Typography, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Chip, Button,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack, Autocomplete,
} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { formatCurrency } from '../utils/currency';
import EmptyState from '../components/EmptyState';
import { API_BASE_URL } from '../utils/media';

const statusColor = { Pending: 'warning', Shipped: 'info', Completed: 'success', Cancelled: 'default' };
const paymentColor = { unpaid: 'warning', paid: 'success', failed: 'error' };

const SellerOrders = () => {
  const [orders, setOrders] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trackingTarget, setTrackingTarget] = useState(null);
  const [trackingForm, setTrackingForm] = useState({ carrierName: '', trackingNumber: '' });
  const { token } = useContext(AuthContext);

  const fetchOrders = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/seller/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch your orders.');
      setOrders(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchCouriers = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/couriers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) setCouriers(await response.json());
    } catch {
      // Non-critical — the tracking dialog just falls back to free text entry.
    }
  }, [token]);

  useEffect(() => { fetchOrders(); fetchCouriers(); }, [fetchOrders, fetchCouriers]);

  const handleSaveTracking = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/seller/orders/${trackingTarget.id}/shipping`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(trackingForm),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save tracking info.');
      setTrackingTarget(null);
      fetchOrders();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) return <CircularProgress size={28} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Orders to Fulfill</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Orders containing your products. Add tracking once an item has shipped.
      </Typography>

      {orders.length === 0 ? (
        <EmptyState icon={<ReceiptLongOutlinedIcon />} title="No orders yet" description="Orders containing your products will appear here." />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell><TableCell>Customer</TableCell><TableCell>Your Items</TableCell>
                <TableCell>Fulfillment</TableCell><TableCell>Ship To</TableCell><TableCell align="right">Total</TableCell>
                <TableCell>Payment</TableCell><TableCell>Status</TableCell><TableCell>Tracking</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell><Link to={`/orders/${order.id}`}>#{order.id}</Link></TableCell>
                  <TableCell>{order.customer_email}</TableCell>
                  <TableCell>{order.my_items}</TableCell>
                  <TableCell><Chip label={order.fulfillment_type === 'collection' ? 'Collection' : 'Delivery'} size="small" variant="outlined" /></TableCell>
                  <TableCell>{order.fulfillment_type === 'collection' ? '—' : [order.shipping_city, order.shipping_country].filter(Boolean).join(', ')}</TableCell>
                  <TableCell align="right">{formatCurrency(order.total_amount)}</TableCell>
                  <TableCell>
                    <Chip label={order.payment_status} size="small" color={paymentColor[order.payment_status] || 'default'} sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell><Chip label={order.status} size="small" color={statusColor[order.status] || 'default'} /></TableCell>
                  <TableCell>
                    {order.tracking_number ? (
                      <Box>
                        <Typography variant="body2">{order.carrier_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{order.tracking_number}</Typography>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        onClick={() => { setTrackingTarget(order); setTrackingForm({ carrierName: '', trackingNumber: '' }); }}
                      >
                        Add Tracking
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(trackingTarget)} onClose={() => setTrackingTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Add Tracking for Order #{trackingTarget?.id}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              freeSolo
              options={couriers.map((c) => c.name)}
              value={trackingForm.carrierName}
              onInputChange={(e, value) => setTrackingForm({ ...trackingForm, carrierName: value })}
              renderInput={(params) => <TextField {...params} label="Carrier / Courier Company" placeholder="Pick from directory or type your own" />}
            />
            <TextField
              label="Tracking Number" fullWidth
              value={trackingForm.trackingNumber}
              onChange={(e) => setTrackingForm({ ...trackingForm, trackingNumber: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackingTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!trackingForm.carrierName || !trackingForm.trackingNumber}
            onClick={handleSaveTracking}
          >
            Save &amp; Mark Shipped
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SellerOrders;
